"use client";
import { motion } from "framer-motion";
import { ArrowRight, Code, Zap, Shield, Cpu, Users, Award, Calendar } from "lucide-react";
import logo from "../assets/Marca MAYMA.png";

const Hero = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <section
      id="inicio"
      className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 relative overflow-hidden"
    >
      {/* Elementos decorativos con más presencia del verde */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-20 w-72 h-72 bg-[#77c593]/8 rounded-full blur-3xl"></div>
        <div className="absolute bottom-32 left-20 w-96 h-96 bg-[#77c593]/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-[#77c593]/3 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-6"
            ></motion.div>

            <motion.h1
              className="text-5xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Potenciamos tu
              <span className="text-[#77c593] block">Transformación</span>
              <span className="bg-gradient-to-r from-[#77c593] to-[#5a9d73] bg-clip-text text-transparent block">Digital</span>
            </motion.h1>

            <motion.p
              className="text-xl text-gray-600 mb-8 leading-relaxed max-w-2xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              Somos especialistas en desarrollo de software a medida. Transformamos procesos empresariales con soluciones tecnológicas innovadoras que escalan con tu negocio.
            </motion.p>

            {/* Stats Section */}
            <motion.div
              className="flex flex-wrap gap-8 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <div className="flex items-center space-x-3">
                <div className="bg-[#77c593]/10 p-2 rounded-lg">
                  <Calendar size={20} className="text-[#77c593]" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">15+</div>
                  <div className="text-sm text-gray-600">Años de experiencia</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="bg-[#77c593]/10 p-2 rounded-lg">
                  <Users size={20} className="text-[#77c593]" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">50+</div>
                  <div className="text-sm text-gray-600">Proyectos completados</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <motion.button
                onClick={() => scrollToSection("contacto")}
                whileHover={{
                  scale: 1.02,
                  boxShadow: "0 10px 30px rgba(119, 197, 147, 0.3)",
                }}
                whileTap={{ scale: 0.98 }}
                className="bg-gradient-to-r from-[#77c593] to-[#5a9d73] text-white px-8 py-4 rounded-xl font-semibold flex items-center justify-center space-x-2 hover:from-[#5a9d73] hover:to-[#77c593] shadow-xl transition-all duration-300"
              >
                <span>Iniciar Proyecto</span>
                <ArrowRight size={20} />
              </motion.button>

              <motion.button
                onClick={() => scrollToSection("servicios")}
                whileHover={{
                  scale: 1.02,
                  borderColor: "#77c593",
                  color: "#77c593",
                }}
                whileTap={{ scale: 0.98 }}
                className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-xl font-semibold hover:bg-[#77c593]/5 transition-all duration-300"
              >
                Ver Servicios
              </motion.button>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <div className="relative z-10">
              {/* Modern innovative logo design */}
              <div className="relative w-80 h-80 mx-auto">
                {/* Hexagonal grid pattern */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 40,
                    repeat: Number.POSITIVE_INFINITY,
                    ease: "linear",
                  }}
                  className="absolute inset-0"
                >
                  {Array.from({ length: 6 }).map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute w-20 h-20 border-2 border-[#77c593]/15"
                      style={{
                        clipPath: 'polygon(30% 0%, 70% 0%, 100% 50%, 70% 100%, 30% 100%, 0% 50%)',
                        top: '50%',
                        left: '50%',
                        transform: `translate(-50%, -50%) rotate(${i * 60}deg) translateY(-100px)`,
                      }}
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.8, 0.3],
                      }}
                      transition={{
                        duration: 4,
                        repeat: Number.POSITIVE_INFINITY,
                        delay: i * 0.3,
                      }}
                    />
                  ))}
                </motion.div>

                {/* Orbital rings with particles */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 35,
                    repeat: Number.POSITIVE_INFINITY,
                    ease: "linear",
                  }}
                  className="absolute inset-4 border border-dashed border-[#77c593]/25 rounded-full"
                />
                
                <motion.div
                  animate={{ rotate: -360 }}
                  transition={{
                    duration: 28,
                    repeat: Number.POSITIVE_INFINITY,
                    ease: "linear",
                  }}
                  className="absolute inset-12 border border-dashed border-[#77c593]/20 rounded-full"
                />

                {/* Central logo with morphing background */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.div
                    className="relative"
                    animate={{
                      scale: [1, 1.05, 1],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Number.POSITIVE_INFINITY,
                      ease: "easeInOut",
                    }}
                  >
                    {/* Morphing background */}
                    <motion.div
                      className="absolute inset-0 w-28 h-28 bg-gradient-to-r from-[#77c593] to-[#5a9d73] rounded-3xl"
                      animate={{
                        borderRadius: ['24px', '50%', '24px'],
                      }}
                      transition={{
                        duration: 6,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: "easeInOut",
                      }}
                    />
                    
                    {/* Glow effect */}
                    <motion.div
                      className="absolute inset-0 w-28 h-28 bg-gradient-to-r from-[#77c593]/40 to-[#5a9d73]/40 rounded-3xl blur-xl"
                      animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.5, 0.8, 0.5],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: "easeInOut",
                      }}
                    />
                    
                    {/* Logo container */}
                    <div className="relative w-28 h-28 bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 flex items-center justify-center overflow-hidden">
                      <motion.img
                        src={logo}
                        alt="Mayma"
                        className="w-16 h-16 object-contain"
                        animate={{
                          scale: [1, 1.1, 1],
                        }}
                        transition={{
                          duration: 5,
                          repeat: Number.POSITIVE_INFINITY,
                          ease: "easeInOut",
                        }}
                      />
                    </div>
                  </motion.div>
                </div>

                {/* Floating tech icons with modern positioning */}
                {[
                  { icon: Code, position: { top: '10%', left: '20%' }, delay: 0 },
                  { icon: Zap, position: { bottom: '15%', left: '15%' }, delay: 1 },
                  { icon: Shield, position: { bottom: '20%', right: '20%' }, delay: 2 },
                  { icon: Cpu, position: { top: '25%', right: '15%' }, delay: 1.5 },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className="absolute"
                    style={item.position}
                    animate={{
                      y: [-8, 8, -8],
                      rotate: [0, 10, 0],
                      scale: [1, 1.1, 1],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Number.POSITIVE_INFINITY,
                      delay: item.delay,
                      ease: "easeInOut",
                    }}
                  >
                    <div className="bg-white/90 backdrop-blur-sm p-3 rounded-2xl shadow-xl border border-white/30">
                      <item.icon size={20} className="text-[#77c593]" />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
